import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { historyAPI, roundsAPI, getImageUrl } from '../services/api';
import FinalLeaderboard from '../components/history/FinalLeaderboard';
import { useAuth } from '../contexts/AuthContext';

export default function HistoryDetail() {
  const { year } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  // Championship rounds for this event (if event_name is set)
  const [champRounds, setChampRounds] = useState([]);
  const [champLeaderboard, setChampLeaderboard] = useState([]);

  useEffect(() => {
    fetchHistoryEntry();
  }, [year]);

  const fetchHistoryEntry = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await historyAPI.getByYear(parseInt(year));
      setEntry(data);

      // If entry has event_name, fetch championship rounds + leaderboard data
      if (data && data.event_name) {
        try {
          const rounds = await roundsAPI.getAll({
            eventName: data.event_name,
            roundType: 'championship',
            status: 'completed'
          });
          const sorted = (rounds || []).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          setChampRounds(sorted);

          // Fetch leaderboard for last round (R2) — it aggregates all rounds
          if (sorted.length > 0) {
            const lastRound = sorted[sorted.length - 1];
            try {
              const lb = await roundsAPI.getLeaderboard(lastRound.id);
              setChampLeaderboard(lb || []);
            } catch {
              // Non-fatal
            }
          }
        } catch {
          // Non-fatal
        }
      }
    } catch (err) {
      console.error('Error loading history entry:', err);
      setError(err.response?.status === 404 ? 'History entry not found' : 'Failed to load history entry');
      setEntry(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/history/edit/${entry.id}`);
  };

  const handleDelete = async () => {
    if (!entry) return;

    try {
      setDeleting(true);
      await historyAPI.delete(entry.id);
      navigate('/history');
    } catch (err) {
      console.error('Error deleting history entry:', err);
      setError('Failed to delete history entry');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleBack = () => {
    navigate('/history');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="pb-8">
        <div className="px-4">
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{error}</h2>
              <Button variant="secondary" onClick={handleBack} className="mt-4">
                Back to History
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      <div className="px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-primary font-semibold hover:text-secondary transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to History
          </button>
        </div>

        {/* Entry Content */}
        <Card>
          {/* Year & Champion Header */}
          <div className="border-b border-gray-200 pb-6 mb-6 text-center md:text-left">
            {/* Year */}
            <div className="text-4xl text-gray-400 font-light mb-2">
              {entry.year}
            </div>

            {/* Course Name */}
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {entry.course_name}
            </h2>

            {/* Subtitle */}
            <p className="text-sm text-gray-500 tracking-wide mb-1">
              CHAMPION GOLFER OF THE YEAR
            </p>

            {/* Champion Name */}
            <p className="text-xl font-bold text-gray-900 mb-4">
              {entry.champion_name}
            </p>

            {/* Bowl Winner */}
            {entry.bowl_winner_name && (
              <>
                <p className="text-sm text-gray-500 tracking-wide mb-1">
                  THE BOWL WINNER
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {entry.bowl_winner_name}
                </p>
              </>
            )}
          </div>

          {/* Content with Winner Photo Wrapping */}
          <div className="mb-6">
            <div className="prose prose-lg max-w-none">
              {/* Winner Photo - Floats Right with Text Wrapping on Desktop */}
              {entry.image_url && (
                <div className="float-right ml-0 sm:ml-6 mb-4 mt-0 sm:mt-0 w-full sm:w-1/2 md:w-2/5 lg:w-1/3">
                  <img
                    src={getImageUrl(entry.image_url)}
                    alt={`${entry.year} Champion - ${entry.champion_name}`}
                    className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxImage(getImageUrl(entry.image_url));
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {(entry.content || '').split('\n\n').filter(p => p.trim()).map((paragraph, index) => (
                <p key={index} className="text-gray-800 mb-4 leading-relaxed text-left">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Clear float */}
            <div className="clear-both"></div>
          </div>

          {/* Team Photo (Bottom of Narrative) */}
          {entry.image_url_2 && (
            <div className="mb-6">
              <img
                src={getImageUrl(entry.image_url_2)}
                alt={`${entry.year} TML Team Photo`}
                className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxImage(getImageUrl(entry.image_url_2));
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Google Photos Link */}
          {entry.google_photos_url && (
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
              <a
                href={entry.google_photos_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-secondary transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                View Photo Gallery
              </a>
            </div>
          )}

          {/* Final Leaderboard — shown when entry has event_name and we have round data */}
          {entry.event_name && champRounds.length > 0 && champLeaderboard.length > 0 && (
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
              <FinalLeaderboard leaderboard={champLeaderboard} rounds={champRounds} />

              {/* Points-race animation (TML 2026) */}
              {entry.event_name === 'TML 2026' && (
                <a
                  href="/round2-2026.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-4 flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl shadow-sm hover:opacity-90 transition-opacity"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">▶</span>
                    <div>
                      <p className="text-sm font-bold">Watch the Round 2 points race</p>
                      <p className="text-xs text-white/80">Hole-by-hole animation from the Round 1 standings</p>
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              )}

              {/* Round links */}
              <div className="space-y-2">
                {champRounds.map((r, i) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/rounds/${r.id}/results`, {
                      state: { backLabel: entry.event_name || `TML ${entry.year}` }
                    })}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-primary hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-left">
                      <p className="font-semibold text-gray-900 text-sm">
                        Championship Round {i + 1}
                      </p>
                      <p className="text-xs text-gray-400">Stableford · Gross · Fivers</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Admin Controls - Moved to Bottom */}
          {isAuthenticated && (
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
              <div className="flex gap-3 mb-4">
                <Button
                  variant="primary"
                  onClick={handleEdit}
                  className="flex-1"
                >
                  Edit Entry
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex-1"
                >
                  Delete Entry
                </Button>
              </div>

              {/* Delete Confirmation */}
              {showDeleteConfirm && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                  <h3 className="text-lg font-bold text-red-900 mb-2">Confirm Deletion</h3>
                  <p className="text-red-800 mb-4">
                    Are you sure you want to delete the {entry.year} history entry? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="danger"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img
            src={lightboxImage}
            alt="Full size view"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-70 rounded-full w-10 h-10 flex items-center justify-center text-2xl font-light hover:bg-opacity-90 transition-all"
            aria-label="Close lightbox"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
